import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Ticket, ArrowRight } from 'lucide-react';
import { COMPANY_CONFIG } from '../config/config';

const Contact = () => {
  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: COMPANY_CONFIG.phone,
      description: 'Call us for any queries',
      link: `tel:${COMPANY_CONFIG.phone}`,
      gradient: 'bg-[#00336a]'
    },
    {
      icon: Mail,
      title: 'Email',
      value: COMPANY_CONFIG.email,
      description: 'Send us an email',
      link: `mailto:${COMPANY_CONFIG.email}`,
      gradient: 'bg-[#00336a]'
    },
    {
      icon: MapPin,
      title: 'Address',
      value: COMPANY_CONFIG.address,
      description: 'Visit our office',
      link: null,
      gradient: 'bg-[#00336a]'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      value: '24/7 Service',
      description: 'Available round the clock',
      link: null,
      gradient: 'bg-[#00336a]'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-50">
      {/* Header Section */}
      <section className="relative bg-[#00336a] text-white py-8 sm:py-12 lg:py-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 sm:w-80 sm:h-80 bg-blue-900 rounded-full opacity-20 blur-2xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-80 sm:h-80 bg-blue-900 rounded-full opacity-20 blur-2xl"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 text-center">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-3 sm:mb-4 drop-shadow-lg">Contact Us</h1>
          <p className="text-sm sm:text-base lg:text-lg text-blue-100 max-w-3xl mx-auto">
            Get in touch with us for any questions, bookings, or support. We're here to help!
          </p>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              const content = (
                <div className={`bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-5 text-center hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-blue-100 ${info.link ? 'cursor-pointer' : ''}`}>
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 ${info.gradient} rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 shadow-md`}>
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-1.5">{info.title}</h3>
                  {info.link ? (
                    <a href={info.link} className="text-[#00336a] font-semibold mb-1 hover:text-blue-700 block text-xs sm:text-sm">
                      {info.value}
                    </a>
                  ) : (
                    <p className="text-[#00336a] font-semibold mb-1 text-xs sm:text-sm">{info.value}</p>
                  )}
                  <p className="text-gray-600 text-xs sm:text-sm">{info.description}</p>
                </div>
              );

              return info.link ? (
                <a key={index} href={info.link} className="block">
                  {content}
                </a>
              ) : (
                <div key={index}>{content}</div>
              );
            })}
          </div>

          {/* Additional Information */}
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
              {/* Office Hours */}
              <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-5 lg:p-6 border border-blue-100 hover:shadow-lg transition-shadow">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 bg-[#00336a] rounded-lg sm:rounded-xl flex items-center justify-center mr-2 sm:mr-3">
                    <Clock className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5 text-white" />
                  </div>
                  Office Hours
                </h3>
                <div className="space-y-2 sm:space-y-2.5">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600 font-medium text-xs sm:text-sm">Monday - Friday</span>
                    <span className="font-bold text-gray-900 text-xs sm:text-sm">8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600 font-medium text-xs sm:text-sm">Saturday</span>
                    <span className="font-bold text-gray-900 text-xs sm:text-sm">9:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600 font-medium text-xs sm:text-sm">Sunday</span>
                    <span className="font-bold text-gray-900 text-xs sm:text-sm">10:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600 font-medium text-xs sm:text-sm">24/7 Support</span>
                    <span className="font-bold text-green-600 text-xs sm:text-sm">Available</span>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-5 lg:p-6 border border-blue-100 hover:shadow-lg transition-shadow">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg sm:rounded-xl flex items-center justify-center mr-2 sm:mr-3">
                    <Phone className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5 text-white" />
                  </div>
                  Emergency Contact
                </h3>
                <p className="text-gray-600 mb-3 leading-relaxed text-xs sm:text-sm">
                  For urgent matters or after-hours support, please call our 24/7 hotline.
                </p>
                <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                  <div className="flex items-center mb-1.5">
                    <Phone className="w-5 h-5 sm:w-5.5 sm:h-5.5 text-red-600 mr-2 sm:mr-3" />
                    <a href={`tel:${COMPANY_CONFIG.phone}`} className="text-red-700 font-bold text-sm sm:text-base hover:text-red-800">
                      {COMPANY_CONFIG.phone}
                    </a>
                  </div>
                  <p className="text-red-600 text-xs sm:text-sm font-medium">Available 24/7</p>
                </div>
              </div>

              {/* Location Info */}
              <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-5 lg:p-6 border border-blue-100 hover:shadow-lg transition-shadow">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 bg-[#00336a] rounded-lg sm:rounded-xl flex items-center justify-center mr-2 sm:mr-3">
                    <MapPin className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5 text-white" />
                  </div>
                  Our Location
                </h3>
                <p className="text-gray-600 leading-relaxed text-xs sm:text-sm mb-2">
                  {COMPANY_CONFIG.address}
                </p>
                <p className="text-gray-500 text-[10px] sm:text-xs">
                  Feel free to visit our office during business hours or call us to schedule an appointment.
                </p>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-5 lg:p-6 border border-blue-100 hover:shadow-lg transition-shadow">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3">Follow Us</h3>
                <p className="text-gray-600 mb-4 leading-relaxed text-xs sm:text-sm">
                  Stay updated with our latest news and offers.
                </p>
                <div className="flex space-x-3 sm:space-x-4">
                  <a 
                    href="https://web.facebook.com/profile.php?id=100072123609644" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 sm:w-11 sm:h-11 lg:w-12 lg:h-12 bg-[#00336a] text-white rounded-lg sm:rounded-xl flex items-center justify-center hover:bg-blue-800 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                  >
                    <span className="font-bold text-sm sm:text-base lg:text-lg">f</span>
                  </a>
                  <a 
                    href="https://www.youtube.com/@NuwanthaEnterprises1321" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 sm:w-11 sm:h-11 lg:w-12 lg:h-12 bg-gradient-to-br from-red-600 to-red-700 text-white rounded-lg sm:rounded-xl flex items-center justify-center hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                  >
                    <span className="font-bold text-sm sm:text-base lg:text-lg">YT</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 sm:py-12 lg:py-16 bg-[#00336a] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-900/50"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-3 sm:mb-4">
            We're Here to Help
          </h2>
          <p className="text-sm sm:text-base lg:text-lg mb-5 sm:mb-6 lg:mb-8 text-blue-100 max-w-3xl mx-auto">
            Whether you have questions about our services, need assistance with bookings, 
            or want to provide feedback, our friendly team is ready to assist you.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://tripimate.com"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#00336a] px-5 sm:px-6 lg:px-8 py-2 sm:py-2.5 lg:py-3 rounded-lg text-xs sm:text-sm lg:text-base font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center justify-center space-x-1.5 border-2 border-blue-300"
            >
              <span>Book Online Now</span>
            </a>
            <a
              href={`tel:${COMPANY_CONFIG.phone}`}
              className="border-2 border-white text-white px-5 sm:px-6 lg:px-8 py-2 sm:py-2.5 lg:py-3 rounded-lg text-xs sm:text-sm lg:text-base font-bold hover:bg-white hover:text-[#00336a] transition-all duration-200 inline-flex items-center justify-center space-x-1.5"
            >
              <Phone className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5" />
              <span>Call Us Now</span>
            </a>
            <a
              href={`mailto:${COMPANY_CONFIG.email}`}
              className="border-2 border-white text-white px-5 sm:px-6 lg:px-8 py-2 sm:py-2.5 lg:py-3 rounded-lg text-xs sm:text-sm lg:text-base font-bold hover:bg-white hover:text-[#00336a] transition-all duration-200 inline-flex items-center justify-center space-x-1.5"
            >
              <Mail className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5" />
              <span>Send Email</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
